/**
 * Horizontal scroll sliders with prev/next controls.
 * Optional autoplay via data-dc-slider-autoplay="{ms}" (e.g. 5000).
 */

function scrollStep(track) {
	const first = track.children[0];
	if (!first) {
		return Math.max(160, track.clientWidth * 0.75);
	}

	const styles = getComputedStyle(track);
	const gap = parseFloat(styles.columnGap || styles.gap) || 0;
	return first.getBoundingClientRect().width + gap;
}

function syncControls(root, track) {
	const prev = root.querySelector('[data-dc-slider-prev]');
	const next = root.querySelector('[data-dc-slider-next]');
	const controls = root.querySelector('[data-dc-slider-controls]');
	const max = track.scrollWidth - track.clientWidth;

	if (controls) {
		controls.hidden = max <= 4;
	}

	if (prev) {
		prev.disabled = track.scrollLeft <= 4;
	}
	if (next) {
		next.disabled = track.scrollLeft >= max - 4;
	}
}

function prefersReducedMotion() {
	return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Same scroll motion as the prev/next buttons. */
function scrollTrackBy(track, delta) {
	track.scrollBy({ left: delta, behavior: 'smooth' });
}

function advanceTrack(track) {
	const max = track.scrollWidth - track.clientWidth;
	if (max <= 4) {
		return;
	}

	const step = scrollStep(track);
	if (track.scrollLeft >= max - 4) {
		track.scrollTo({ left: 0, behavior: 'smooth' });
		return;
	}

	scrollTrackBy(track, step);
}

/**
 * @param {HTMLElement} root
 * @param {HTMLElement} track
 * @param {number} intervalMs
 */
function bindAutoplay(root, track, intervalMs) {
	if (intervalMs < 1000 || prefersReducedMotion()) {
		return;
	}

	let timerId = 0;
	let paused = false;
	let pointerInside = false;
	let focusInside = false;

	const clear = () => {
		if (timerId) {
			window.clearTimeout(timerId);
			timerId = 0;
		}
	};

	const schedule = () => {
		clear();
		if (paused || pointerInside || focusInside) {
			return;
		}
		if (track.scrollWidth - track.clientWidth <= 4) {
			return;
		}
		timerId = window.setTimeout(() => {
			timerId = 0;
			if (paused || pointerInside || focusInside) {
				return;
			}
			advanceTrack(track);
			schedule();
		}, intervalMs);
	};

	const pause = () => {
		paused = true;
		clear();
	};

	const resume = () => {
		paused = false;
		schedule();
	};

	/** Restart the countdown after a manual move so autoplay never fights the user. */
	const bumpAfterManual = () => {
		clear();
		schedule();
	};

	root.addEventListener('mouseenter', () => {
		pointerInside = true;
		clear();
	});
	root.addEventListener('mouseleave', () => {
		pointerInside = false;
		schedule();
	});

	root.addEventListener('focusin', () => {
		focusInside = true;
		clear();
	});
	root.addEventListener('focusout', (event) => {
		const next = event.relatedTarget;
		if (next instanceof Node && root.contains(next)) {
			return;
		}
		focusInside = false;
		schedule();
	});

	document.addEventListener('visibilitychange', () => {
		if (document.hidden) {
			clear();
			return;
		}
		schedule();
	});

	track.addEventListener(
		'pointerdown',
		() => {
			pause();
		},
		{ passive: true }
	);
	track.addEventListener(
		'pointerup',
		() => {
			resume();
		},
		{ passive: true }
	);
	track.addEventListener(
		'pointercancel',
		() => {
			resume();
		},
		{ passive: true }
	);

	root.addEventListener('dc-slider:manual', bumpAfterManual);

	schedule();
}

function bindSlider(root) {
	const track = root.querySelector('[data-dc-slider-track]');
	if (!track || root.dataset.dcSliderReady === '1') {
		return;
	}

	root.dataset.dcSliderReady = '1';

	const prev = root.querySelector('[data-dc-slider-prev]');
	const next = root.querySelector('[data-dc-slider-next]');

	const notifyManual = () => {
		root.dispatchEvent(new CustomEvent('dc-slider:manual'));
	};

	prev?.addEventListener('click', () => {
		notifyManual();
		scrollTrackBy(track, -scrollStep(track));
	});

	next?.addEventListener('click', () => {
		notifyManual();
		scrollTrackBy(track, scrollStep(track));
	});

	const update = () => syncControls(root, track);
	track.addEventListener('scroll', update, { passive: true });
	window.addEventListener('resize', update);

	if (typeof ResizeObserver !== 'undefined') {
		const observer = new ResizeObserver(update);
		observer.observe(track);
	}

	update();

	const autoplayMs = Number.parseInt(root.getAttribute('data-dc-slider-autoplay') || '', 10);
	if (Number.isFinite(autoplayMs) && autoplayMs > 0) {
		bindAutoplay(root, track, autoplayMs);
	}
}

export function initSliders() {
	document.querySelectorAll('[data-dc-slider]').forEach(bindSlider);
}
